import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Reveal } from "../common/Reveal";
import { contactService } from "../../services/contactService";
import { useLanguage } from "../../context/LanguageContext";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY = {
  company: "",
  contactName: "",
  email: "",
  country: "",
  businessType: "",
  message: "",
};

export default function PartnershipForm() {
  const { t } = useLanguage();
  const [data, setData] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const businessTypes = t("form.businessTypes");
  const err = t("form.errors");

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setData((d) => ({ ...d, [k]: v }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!data.company.trim()) e.company = err.required;
    if (!data.contactName.trim()) e.contactName = err.required;
    if (!data.email.trim()) e.email = err.required;
    else if (!EMAIL_RE.test(data.email)) e.email = err.email;
    if (!data.country.trim()) e.country = err.required;
    if (!data.businessType) e.businessType = err.required;
    if (data.message.trim().length < 10) e.message = err.message;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await contactService.submitPartnershipRequest(data);
      setDone(true);
      setData(EMPTY);
      toast.success(t("form.successTitle"), { description: t("form.success") });
    } catch {
      toast.error(t("form.failTitle"), { description: t("form.fail") });
    } finally {
      setLoading(false);
    }
  };

  const field =
    "w-full bg-transparent border-b border-[#F6F2EC]/25 py-3 text-[#F6F2EC] placeholder-[#F6F2EC]/40 focus:outline-none focus:border-[#C4A46B] transition-colors";
  const errClass = "text-[#D9A9AE] text-xs mt-1";

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#0D0D0D]" data-testid="contact-section">
      <div className="container-tessa grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] text-[#F6F2EC]">
              {t("form.title1")}<br />
              <span className="italic text-[#D9A9AE]">{t("form.title2")}</span>
            </h2>
            <p className="mt-6 text-[#F6F2EC]/70 leading-relaxed max-w-md">
              {t("form.subtitle")}
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <AnimatePresence mode="wait">
            {done ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="h-full min-h-[300px] flex flex-col items-start justify-center"
                data-testid="form-success"
              >
                <span className="grid place-items-center h-14 w-14 rounded-full bg-[#C4A46B] text-black mb-6">
                  <Check size={26} />
                </span>
                <h3 className="font-serif text-3xl text-[#F6F2EC]">{t("form.successTitle")}</h3>
                <p className="mt-3 text-[#F6F2EC]/75 max-w-md leading-relaxed">{t("form.success")}</p>
                <button
                  onClick={() => setDone(false)}
                  data-testid="form-reset"
                  className="mt-8 text-[12px] uppercase tracking-[0.16em] font-semibold text-[#C4A46B] border-b border-[#C4A46B] pb-1"
                >
                  {t("form.submit")}
                </button>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onSubmit={onSubmit}
                noValidate
                className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-7"
                data-testid="partnership-form"
              >
                <div>
                  <label htmlFor="pf-company" className="block text-[11px] uppercase tracking-[0.16em] text-[#F6F2EC]/60 mb-1">
                    {t("form.company")}
                  </label>
                  <input id="pf-company" data-testid="input-company" aria-invalid={!!errors.company} className={field} value={data.company} onChange={set("company")} />
                  {errors.company && <p role="alert" className={errClass}>{errors.company}</p>}
                </div>

                <div>
                  <label htmlFor="pf-name" className="block text-[11px] uppercase tracking-[0.16em] text-[#F6F2EC]/60 mb-1">
                    {t("form.contactName")}
                  </label>
                  <input id="pf-name" data-testid="input-contact-name" aria-invalid={!!errors.contactName} className={field} value={data.contactName} onChange={set("contactName")} />
                  {errors.contactName && <p role="alert" className={errClass}>{errors.contactName}</p>}
                </div>

                <div>
                  <label htmlFor="pf-email" className="block text-[11px] uppercase tracking-[0.16em] text-[#F6F2EC]/60 mb-1">
                    {t("form.email")}
                  </label>
                  <input id="pf-email" type="email" data-testid="input-email" aria-invalid={!!errors.email} className={field} value={data.email} onChange={set("email")} />
                  {errors.email && <p role="alert" className={errClass}>{errors.email}</p>}
                </div>

                <div>
                  <label htmlFor="pf-country" className="block text-[11px] uppercase tracking-[0.16em] text-[#F6F2EC]/60 mb-1">
                    {t("form.country")}
                  </label>
                  <input id="pf-country" data-testid="input-country" aria-invalid={!!errors.country} className={field} value={data.country} onChange={set("country")} />
                  {errors.country && <p role="alert" className={errClass}>{errors.country}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-[11px] uppercase tracking-[0.16em] text-[#F6F2EC]/60 mb-2">
                    {t("form.businessType")}
                  </label>
                  <Select value={data.businessType} onValueChange={set("businessType")}>
                    <SelectTrigger
                      data-testid="select-business-type"
                      className="w-full bg-transparent border-0 border-b border-[#F6F2EC]/25 rounded-none px-0 py-3 h-auto text-[#F6F2EC] focus:ring-0 focus:border-[#C4A46B]"
                    >
                      <SelectValue placeholder={t("form.businessTypePlaceholder")} />
                    </SelectTrigger>
                    <SelectContent>
                      {businessTypes.map((bt) => (
                        <SelectItem key={bt} value={bt} data-testid={`business-type-${bt}`}>
                          {bt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.businessType && <p role="alert" className={errClass}>{errors.businessType}</p>}
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="pf-message" className="block text-[11px] uppercase tracking-[0.16em] text-[#F6F2EC]/60 mb-1">
                    {t("form.message")}
                  </label>
                  <textarea id="pf-message" rows={3} data-testid="input-message" aria-invalid={!!errors.message} className={`${field} resize-none`} value={data.message} onChange={set("message")} />
                  {errors.message && <p role="alert" className={errClass}>{errors.message}</p>}
                </div>

                <div className="md:col-span-2">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="partnership-submit-button"
                    className="inline-flex items-center justify-center gap-2 h-14 px-10 bg-[#D92A47] text-[#F6F2EC] text-[13px] font-semibold uppercase tracking-[0.14em] hover:bg-[#b71e3a] transition-colors disabled:opacity-70"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={17} className="animate-spin" /> {t("form.sending")}
                      </>
                    ) : (
                      t("form.submit")
                    )}
                  </button>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
